import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../api/axios";
import { fetchAllUsers } from "../../api/fetchAllUsers";
import { ROLES } from "../../constants/roles";

const facilityOptions = [
  "Parking",
  "South Facing",
  "AC",
  "Elevator",
  "Security",
  "Generator",
  "Community Hall",
  "Masjid",
  "Kids Zone",
  "Swiming Pool"

];

const measurementFields = ["area_sq_ft", "katha", "bigha", "acre"];

const emptyForm = (propertyId) => ({
  property_id: propertyId,
  type_id: "",
  purpose: "Rent",
  unit_number: "",
  floor: "",
  price: "",
  rent_amount: "",
  tenant_id: "",
  status_id: "",
  marketing_officer_id: "",
  marketing_manager_id: "",
  facilities: [],
  measurements: {
    area_sq_ft: "",
    katha: "",
    bigha: "",
    acre: "",
    unit_of_measure: "sq-ft",
  },
});

const extractMeasurement = (unit) => {
  if (!unit?.measurement) return null;
  if (Array.isArray(unit.measurement)) {
    return unit.measurement.length ? unit.measurement[0] : null;
  }
  return unit.measurement;
};

export default function UnitModal({
  show,
  propertyId,
  property,
  unit,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(() => emptyForm(propertyId));
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Photo management state
  const [newPhotos, setNewPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);

  // Owner & Manager dropdown users
  const [owners, setOwners] = useState([]);
  const [managers, setManagers] = useState([]);
  const [marketingOfficers, setMarketingOfficers] = useState([]);
  const [marketingManagers, setMarketingManagers] = useState([]);
  const [agents, setAgents] = useState([]);

  //property status
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  useEffect(() => {
    const loadStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const { data } = await api.get("/api/property-statuses");
        setStatuses(Array.isArray(data?.data) ? data.data : data);
      } catch (error) {
        toast.error("Failed to load statuses");
      } finally {
        setLoadingStatuses(false);
      }
    };
    loadStatuses();

    const fetchUsers = async () => {
      try {
        const users = await fetchAllUsers();
        setOwners(users);
        setManagers(users);

        // Fetch marketing officer and manager options (full list)
        const [moRes, mmRes] = await Promise.all([
          api.get(`/api/users/by-role/${encodeURIComponent(ROLES.MARKETING_OFFICER)}`),
          api.get(`/api/users/by-role/${encodeURIComponent(ROLES.MARKETING_MANAGER)}`),
        ]);
        setMarketingOfficers(moRes.data || []);
        setMarketingManagers(mmRes.data || []);
        // "Agent" is not a separate role: agents are the Marketing Officers.
        setAgents(moRes.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch users data!");
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    setForm(emptyForm(propertyId));
    console.log("Unit Form Data", form);
  }, [propertyId]);

  useEffect(() => {
    const loadTypes = async () => {
      setLoadingTypes(true);
      try {
        const { data } = await api.get("/api/property-types");
        setTypes(Array.isArray(data?.data) ? data.data : data);
      } catch (error) {
        toast.error("Failed to load property types");
      } finally {
        setLoadingTypes(false);
      }
    };
    loadTypes();
  }, []);

  useEffect(() => {
    if (unit) {
      const measurement = extractMeasurement(unit);
      setForm({
        property_id: unit.property_id ?? propertyId,
        type_id: unit.type_id ?? "",
        purpose: unit.purpose ?? "Rent",
        unit_number: unit.unit_number ?? "",
        floor: unit.floor ?? "",
        price: unit.price ?? "",
        rent_amount: unit.rent_amount ?? "",

        // Owner & Manager
        owner_id: unit.owner_id || "",
        manager_id: unit.manager_id || "",
        marketing_officer_id: unit.marketing_officer_id || "",
        marketing_manager_id: unit.marketing_manager_id || "",
        agent_id: unit.agent_id || "",


        status_id: unit.status_id ?? "",
        tenant_id: unit.tenant_id ?? "",
        facilities: Array.isArray(unit.facilities)
          ? unit.facilities
          : unit.facilities
            ? Object.values(unit.facilities)
            : [],
        measurements: {
          area_sq_ft: measurement?.area_sq_ft ?? "",
          katha: measurement?.katha ?? "",
          bigha: measurement?.bigha ?? "",
          acre: measurement?.acre ?? "",
          unit_of_measure: measurement?.unit_of_measure ?? "sq-ft",
        },
      });

      // Load existing photos
      if (unit.photos && Array.isArray(unit.photos)) {
        setExistingPhotos(unit.photos);
      } else if (unit.photos) {
        setExistingPhotos([unit.photos]);
      } else {
        setExistingPhotos([]);
      }
    } else {
      setForm(emptyForm(propertyId));
      setExistingPhotos([]);
      setNewPhotos([]);
      setPhotoPreviews([]);
    }
  }, [unit, propertyId]);

  const measurementId = useMemo(() => {
    const measurement = extractMeasurement(unit);
    return measurement?.id;
  }, [unit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    console.log("manager Id and owner Id", form.manager_id, form.owner_id)
  };

  const handleMeasurementChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      measurements: { ...prev.measurements, [name]: value },
    }));
  };

  const handleFacilitiesChange = (e) => {
    const { value, checked } = e.target;
    setForm((prev) => {
      const facilities = new Set(prev.facilities);
      if (checked) facilities.add(value);
      else facilities.delete(value);
      return { ...prev, facilities: Array.from(facilities) };
    });
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const totalPhotos = existingPhotos.length + newPhotos.length + files.length;
    if (totalPhotos > 10) {
      toast.error(`Maximum 10 photos allowed. You have ${existingPhotos.length + newPhotos.length} photos and trying to add ${files.length} more.`);
      return;
    }

    const fileArray = Array.from(files);
    setNewPhotos([...newPhotos, ...fileArray]);

    // Create previews
    const newPreviews = [];
    fileArray.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({ file, preview: e.target.result });
        if (newPreviews.length === fileArray.length) {
          setPhotoPreviews([...photoPreviews, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove new photo (before upload)
  const removeNewPhoto = (index) => {
    const updated = newPhotos.filter((_, i) => i !== index);
    setNewPhotos(updated);
    const updatedPreviews = photoPreviews.filter((_, i) => i !== index);
    setPhotoPreviews(updatedPreviews);
  };

  // Delete existing photo
  const deletePhoto = async (photoId) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;

    try {
      await api.delete(`/api/units/${unit.id}/photos/${photoId}`);
      setExistingPhotos(existingPhotos.filter((p) => p.id !== photoId));
      toast.success("Photo deleted successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete photo");
    }
  };

  // Set primary photo
  const setPrimaryPhoto = async (photoId) => {
    try {
      await api.put(`/api/units/${unit.id}/photos/${photoId}/primary`);
      setExistingPhotos(
        existingPhotos.map((p) => ({
          ...p,
          is_primary: p.id === photoId,
        }))
      );
      toast.success("Primary photo updated");
    } catch (error) {
      console.error(error);
      toast.error("Failed to set primary photo");
    }
  };

  // Reorder photos
  const reorderPhotos = async (photoIds) => {
    try {
      await api.put(`/api/units/${unit.id}/photos/reorder`, {
        photo_ids: photoIds,
      });
      // Reorder existing photos array
      const reordered = photoIds
        .map((id) => existingPhotos.find((p) => p.id === id))
        .filter(Boolean);
      setExistingPhotos(reordered);
      toast.success("Photos reordered");
    } catch (error) {
      console.error(error);
      toast.error("Failed to reorder photos");
    }
  };

  // Move photo up/down
  const movePhoto = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === existingPhotos.length - 1)
    ) {
      return;
    }

    const newOrder = [...existingPhotos];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newOrder[index], newOrder[targetIndex]] = [
      newOrder[targetIndex],
      newOrder[index],
    ];
    setExistingPhotos(newOrder);

    // Update display_order on backend
    const photoIds = newOrder.map((p) => p.id);
    reorderPhotos(photoIds);
  };

  const hasMeasurementValues = () => {
    return measurementFields.some((field) => form.measurements[field]);
  };

  const sanitizeNumber = (value) => {
    if (value === "" || value === null || typeof value === "undefined")
      return null;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("property_id", propertyId);
      formData.append("type_id", form.type_id);
      formData.append("purpose", form.purpose);

      formData.append("owner_id", form.owner_id);
      formData.append("manager_id", form.manager_id);
      if (form.marketing_officer_id)
        formData.append("marketing_officer_id", form.marketing_officer_id);
      if (form.marketing_manager_id)
        formData.append("marketing_manager_id", form.marketing_manager_id);
      if (form.agent_id)
        formData.append("agent_id", form.agent_id);

      formData.append("unit_number", form.unit_number);
      if (form.floor !== "" && form.floor !== null)
        formData.append("floor", form.floor);
      if (form.price !== "" && form.price !== null)
        formData.append("price", form.price);
      if (form.rent_amount !== "" && form.rent_amount !== null) {
        formData.append("rent_amount", form.rent_amount);
      }
      if (form.tenant_id) formData.append("tenant_id", form.tenant_id);
      form.facilities.forEach((facility) =>
        formData.append("facilities[]", facility)
      );

      // Add new photos
      if (newPhotos.length > 0) {
        newPhotos.forEach((photo) => {
          formData.append("photos[]", photo);
        });
      }

      let unitResponse;
      if (unit?.id) {
        formData.append("_method", "PUT");
        unitResponse = await api.post(`/api/units/${unit.id}`, formData);
      } else {
        unitResponse = await api.post("/api/units", formData);
      }

      const savedUnitId = unit?.id ?? unitResponse?.data?.unit?.id;
      if (!savedUnitId) {
        throw new Error("Unable to determine unit id after save.");
      }

      // Upload new photos if any
      if (newPhotos.length > 0) {
        const photoFormData = new FormData();
        newPhotos.forEach((photo) => {
          photoFormData.append("photos[]", photo);
        });
        await api.post(`/api/units/${savedUnitId}/photos`, photoFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const measurementPayload = {
        unit_id: savedUnitId,
        area_sq_ft: sanitizeNumber(form.measurements.area_sq_ft),
        katha: sanitizeNumber(form.measurements.katha),
        bigha: sanitizeNumber(form.measurements.bigha),
        acre: sanitizeNumber(form.measurements.acre),
        unit_of_measure: form.measurements.unit_of_measure || "sq-ft",
      };

      if (hasMeasurementValues() || measurementId) {
        if (measurementId) {
          await api.put(
            `/api/unit-measurements/${measurementId}`,
            measurementPayload
          );
        } else {
          await api.post("/api/unit-measurements", measurementPayload);
        }
      }

      toast.success(`Unit ${unit ? "updated" : "created"} successfully`);
      await onSaved();
    } catch (error) {
      console.error(error);
      toast.error("Failed to save unit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{unit ? "Edit Unit" : "Add Unit"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Property</Form.Label>
                <Form.Control value={property?.name || propertyId} disabled />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Unit Type</Form.Label>
                <Form.Select
                  name="type_id"
                  value={form.type_id}
                  onChange={handleInputChange}
                  required
                  disabled={loadingTypes}
                >
                  <option value="">Select type</option>
                  {types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Purpose</Form.Label>
                <Form.Select
                  name="purpose"
                  value={form.purpose}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Rent">Rent</option>
                  <option value="Sale">Sale</option>
                  <option value="Lease">Lease</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Unit Number</Form.Label>
                <Form.Control
                  name="unit_number"
                  value={form.unit_number}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Floor</Form.Label>
                <Form.Control
                  name="floor"
                  value={form.floor}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>
          {/* Manager and Owner */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Owner</Form.Label>
                <Form.Select
                  name="owner_id"
                  value={form.owner_id || ""}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select owner</option>
                  {owners.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Manager</Form.Label>
                <Form.Select
                  name="manager_id"
                  value={form.manager_id || ""}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select manager</option>
                  {managers.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">

            <Col md={6}>
              <Form.Group>
                <Form.Label>Marketing Officer</Form.Label>
                <Form.Select
                  name="marketing_officer_id"
                  value={form.marketing_officer_id || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select officer</option>
                  {marketingOfficers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Marketing Manager</Form.Label>
                <Form.Select
                  name="marketing_manager_id"
                  value={form.marketing_manager_id || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select manager</option>
                  {marketingManagers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>


            <Col md={6}>
              <Form.Group>
                <Form.Label>Agent</Form.Label>
                <Form.Select
                  name="agent_id"
                  value={form.agent_id || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select Agent</option>
                  {agents.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>


          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Price (Sale/Lease)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="price"
                  value={form.price}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Rent Amount</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="rent_amount"
                  value={form.rent_amount}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row lassName="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Unit Status</Form.Label>
                <Form.Select
                  name="status_id"
                  value={form.status_id}
                  onChange={handleInputChange}
                  required
                  disabled={loadingStatuses}
                >
                  <option value="">Select status</option>
                  {statuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.status_type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Tenant ID (optional)</Form.Label>
                <Form.Control
                  name="tenant_id"
                  value={form.tenant_id}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Unit Photos (Max 10) - {existingPhotos.length + newPhotos.length}/10
                </Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  disabled={existingPhotos.length + newPhotos.length >= 10}
                />
                <Form.Text className="text-muted">
                  {existingPhotos.length + newPhotos.length >= 10
                    ? "Maximum 10 photos reached"
                    : `You can add ${10 - (existingPhotos.length + newPhotos.length)} more photos`}
                </Form.Text>

                {/* Existing Photos Grid */}
                {existingPhotos.length > 0 && (
                  <div className="mt-3">
                    <h6>Existing Photos</h6>
                    <div className="row g-2">
                      {existingPhotos.map((photo, index) => (
                        <div key={photo.id} className="col-md-3 position-relative">
                          <div className="card">
                            <img
                              src={photo.file_path.startsWith("http") ? photo.file_path : `${api.defaults.baseURL}/${photo.file_path}`}
                              alt={`Photo ${index + 1}`}
                              className="card-img-top"
                              style={{ height: "150px", objectFit: "cover" }}
                            />
                            <div className="card-body p-2">
                              {photo.is_primary && (
                                <span className="badge bg-primary mb-1">Primary</span>
                              )}
                              <div className="btn-group btn-group-sm w-100" role="group">
                                {index > 0 && (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => movePhoto(index, "up")}
                                    title="Move up"
                                  >
                                    ↑
                                  </Button>
                                )}
                                {index < existingPhotos.length - 1 && (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => movePhoto(index, "down")}
                                    title="Move down"
                                  >
                                    ↓
                                  </Button>
                                )}
                                {!photo.is_primary && (
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => setPrimaryPhoto(photo.id)}
                                    title="Set as primary"
                                  >
                                    ⭐
                                  </Button>
                                )}
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => deletePhoto(photo.id)}
                                  title="Delete"
                                >
                                  ×
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Photos Preview */}
                {newPhotos.length > 0 && (
                  <div className="mt-3">
                    <h6>New Photos (will be uploaded)</h6>
                    <div className="row g-2">
                      {newPhotos.map((photo, index) => {
                        const preview = photoPreviews.find((p) => p.file === photo);
                        return (
                          <div key={index} className="col-md-3 position-relative">
                            <div className="card">
                              <img
                                src={preview?.preview || URL.createObjectURL(photo)}
                                alt={`New photo ${index + 1}`}
                                className="card-img-top"
                                style={{ height: "150px", objectFit: "cover" }}
                              />
                              <div className="card-body p-2">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="w-100"
                                  onClick={() => removeNewPhoto(index)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-4">
            <Form.Label>Facilities</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {facilityOptions.map((facility) => (
                <Form.Check
                  key={facility}
                  type="checkbox"
                  label={facility}
                  value={facility}
                  checked={form.facilities.includes(facility)}
                  onChange={handleFacilitiesChange}
                />
              ))}
            </div>
          </Form.Group>

          <div className="border rounded p-3">
            <h6 className="fw-bold mb-3">Measurements</h6>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Area (sq-ft)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="area_sq_ft"
                    value={form.measurements.area_sq_ft}
                    onChange={handleMeasurementChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Katha</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="katha"
                    value={form.measurements.katha}
                    onChange={handleMeasurementChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Bigha</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="bigha"
                    value={form.measurements.bigha}
                    onChange={handleMeasurementChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Acre</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="acre"
                    value={form.measurements.acre}
                    onChange={handleMeasurementChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group>
              <Form.Label>Unit of Measure</Form.Label>
              <Form.Select
                name="unit_of_measure"
                value={form.measurements.unit_of_measure}
                onChange={handleMeasurementChange}
              >
                <option value="sq-ft">Square Feet</option>
                <option value="katha">Katha</option>
                <option value="bigha">Bigha</option>
                <option value="acre">Acre</option>
              </Form.Select>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              "Save Unit"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
